import { Button, Dialog } from '@etg/wings';
import { DangerousHtmlContent, Pictogram, variants } from '@eti/components';
import { useSiteContext, useTranslation } from '@eti/providers';
import { css, mediaQueries } from '@eti/styles';
import type { MutableRefObject } from 'react';
import { useCallback, useEffect, useRef } from 'react';
import { getTranslatedTexts } from '@eti/products';
import { useToggle } from 'usehooks-ts';
import type { CustomerPhoneNumberDetailsType } from '../../models';
import type { ConnectedMergeProps as PhonePrefixChangeConfirmationDialogProps } from './PhonePrefixChangeConfirmationDialogContainer';

const buttonsWrapperStyles = css`
  display: flex;
  flex-direction: column;
  gap: 8px;
  justify-content: end;
  width: 100%;

  @media ${mediaQueries.large.up} {
    flex-direction: row;
  }
`;

const buttonClass = css`
  flex: 1 0 auto;
`;

const texts = {
  closeButtonLabel: 'ScreenReader.CloseButtonDialog.AriaLabel.Text',
  declineButtonLabel: 'TravelerDetails.PhonePrefixChangeConfirmationDialog.Label.Decline',
  confirmButtonLabel: 'TravelerDetails.PhonePrefixChangeConfirmationDialog.Label.Confirm',
  confirmationModalTitle: 'TravelerDetails.PhonePrefixChangeConfirmationDialog.Title',
  confirmationModalDescription: 'TravelerDetails.PhonePrefixChangeConfirmationDialog.Description',
};

export const PhonePrefixChangeConfirmationDialog = ({
  getCGProducts,
  phoneNumber,
  isCgProductAddedInCart,
  removeCgProductFromCart,
  updateReduxPhoneAndPrefix,
  fetchedCGProducts,
}: PhonePrefixChangeConfirmationDialogProps) => {
  const { t } = useTranslation();
  const { brand } = useSiteContext();
  const [shouldShowModal, toggleDialog] = useToggle(false);
  const isFirstRender: MutableRefObject<boolean> = useRef(true);

  const prevPhoneDetailsRef: MutableRefObject<CustomerPhoneNumberDetailsType> = useRef(phoneNumber);
  const {
    closeButtonLabel,
    declineButtonLabel,
    confirmButtonLabel,
    confirmationModalTitle,
    confirmationModalDescription,
  } = getTranslatedTexts(texts, t);

  const getCoverGeniusProducts = useCallback(
    ({
      customerPhoneNumber,
      customerPhoneNumberPrefix,
    }: Exclude<CustomerPhoneNumberDetailsType, null>) => {
      getCGProducts({
        variables: {
          customerPhoneNumber,
          customerPhoneNumberPrefix,
          productIds: [],
        },
      });
    },
    [getCGProducts],
  );

  useEffect(() => {
    if (isCgProductAddedInCart && fetchedCGProducts) {
      removeCgProductFromCart(fetchedCGProducts);
    }
  }, [isCgProductAddedInCart, fetchedCGProducts, removeCgProductFromCart]);

  useEffect(() => {
    if (!phoneNumber) {
      return undefined;
    }

    const prevPhoneDetails = prevPhoneDetailsRef.current;
    if (!prevPhoneDetails && isFirstRender) {
      getCoverGeniusProducts(phoneNumber);
      prevPhoneDetailsRef.current = phoneNumber;
      isFirstRender.current = false;
      return undefined;
    }

    const { customerPhoneNumberPrefix: currentPrefix, customerPhoneNumber: currentNumber } =
      phoneNumber;
    const { customerPhoneNumberPrefix: prevPrefix, customerPhoneNumber: prevNumber } =
      prevPhoneDetailsRef.current ?? {};
    const isPrefixChanged = currentPrefix !== prevPrefix;
    const isNumberChanged = currentNumber !== prevNumber;

    if (!isPrefixChanged && !isNumberChanged) {
      return undefined;
    }

    if (isPrefixChanged && isCgProductAddedInCart) {
      toggleDialog();
    } else {
      getCoverGeniusProducts(phoneNumber);
      prevPhoneDetailsRef.current = phoneNumber;
    }
    return undefined;
  }, [phoneNumber, isCgProductAddedInCart, getCoverGeniusProducts, toggleDialog]);

  const onConfirm = () => {
    toggleDialog();
    removeCgProductFromCart(fetchedCGProducts);
    getCoverGeniusProducts(phoneNumber!);
    prevPhoneDetailsRef.current = phoneNumber;
  };

  const onDecline = () => {
    updateReduxPhoneAndPrefix(prevPhoneDetailsRef.current!);
    toggleDialog();
  };

  return (
    <Dialog
      closeButtonAriaLabel={closeButtonLabel}
      footer={
        <div className={buttonsWrapperStyles}>
          <Button className={buttonClass} onClick={onDecline} variant={variants.SECONDARY}>
            {declineButtonLabel}
          </Button>
          <Button className={buttonClass} onClick={onConfirm} variant={variants.PRIMARY}>
            {confirmButtonLabel}
          </Button>
        </div>
      }
      illustration={<Pictogram brand={brand.code} id="generic-error" />}
      isOpen={shouldShowModal}
      onDismiss={onDecline}
      title={confirmationModalTitle}
    >
      <DangerousHtmlContent
        className="etiModal__content"
        content={confirmationModalDescription}
        data-testid="cg-phone-change-dialog-content"
      />
    </Dialog>
  );
};
